import { getKoreanName } from '../data/countries.js';
import { clearAll, getOverall, getWeakest, type CountryStat } from '../storage.js';

export interface StatsPanelHandlers {
  onClose: () => void;
  onStartReview: () => void;
}

function flagUrl(iso2: string): string {
  return `https://flagcdn.com/h40/${iso2.toLowerCase()}.png`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return ch;
    }
  });
}

export class StatsPanel {
  private readonly root: HTMLElement;
  private handlers: StatsPanelHandlers | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.innerHTML = `<div class="stats-panel" hidden></div>`;
  }

  setHandlers(h: StatsPanelHandlers): void {
    this.handlers = h;
  }

  show(): void {
    const panel = this.panel();
    panel.innerHTML = this.renderHtml();
    panel.hidden = false;
    this.wireEvents();
  }

  hide(): void {
    this.panel().hidden = true;
  }

  private panel(): HTMLElement {
    const el = this.root.querySelector<HTMLElement>('.stats-panel');
    if (!el) throw new Error('StatsPanel: .stats-panel 요소 누락');
    return el;
  }

  private renderHtml(): string {
    const overall = getOverall();
    const weakest = getWeakest(10);
    const pct = overall.totalQuestions === 0
      ? 0
      : Math.round((overall.totalCorrect / overall.totalQuestions) * 100);

    const isEmpty = overall.totalQuestions === 0;
    const emptyHtml = `
      <div class="stats-empty">아직 기록이 없어. 한 라운드 플레이해봐!</div>
      <div class="stats-actions">
        <button type="button" class="stats-close-btn">닫기</button>
      </div>
    `;
    const filledHtml = `
      <div class="stats-summary">
        <div class="stats-summary-row">
          <span class="stats-label">완료한 라운드</span>
          <span class="stats-val">${overall.rounds}</span>
        </div>
        <div class="stats-summary-row">
          <span class="stats-label">누적 정답률</span>
          <span class="stats-val">${pct}% <span class="stats-fraction">(${overall.totalCorrect}/${overall.totalQuestions})</span></span>
        </div>
      </div>
      <div class="stats-section-title">자주 틀린 나라</div>
      ${this.renderWeakest(weakest)}
      <div class="stats-actions">
        <button type="button" class="stats-clear-btn">기록 지우기</button>
        <button type="button" class="stats-review-btn">복습 시작</button>
      </div>
    `;

    return `
      <div class="stats-card">
        <div class="stats-header">
          <h2 class="stats-title">내 기록</h2>
          <button type="button" class="stats-close" aria-label="닫기">✕</button>
        </div>
        ${isEmpty ? emptyHtml : filledHtml}
      </div>
    `;
  }

  private renderWeakest(weakest: Array<{ iso2: string; stat: CountryStat }>): string {
    if (weakest.length === 0) {
      return `<div class="stats-empty-small">통계가 더 쌓이면 여기에 나와! (한 나라당 2번 이상 만나야 집계됨)</div>`;
    }
    const items = weakest.map(({ iso2, stat }) => {
      const pct = Math.round((stat.correct / stat.attempts) * 100);
      const name = escapeHtml(getKoreanName(iso2));
      return `
        <li class="stats-weak-row">
          <img class="stats-flag" src="${flagUrl(iso2)}" alt="" onerror="this.style.display='none'" />
          <span class="stats-country">${name}</span>
          <span class="stats-stat">${stat.correct}/${stat.attempts} (${pct}%)</span>
        </li>
      `;
    }).join('');
    return `<ul class="stats-weak-list">${items}</ul>`;
  }

  private wireEvents(): void {
    const panel = this.panel();
    const close = (): void => {
      this.hide();
      this.handlers?.onClose();
    };
    panel.querySelector<HTMLButtonElement>('.stats-close')
      ?.addEventListener('click', close);
    panel.querySelector<HTMLButtonElement>('.stats-close-btn')
      ?.addEventListener('click', close);

    panel.querySelector<HTMLButtonElement>('.stats-clear-btn')
      ?.addEventListener('click', () => {
        if (window.confirm('모든 기록을 지울까? 되돌릴 수 없어.')) {
          clearAll();
          this.show(); // 재렌더
        }
      });

    panel.querySelector<HTMLButtonElement>('.stats-review-btn')
      ?.addEventListener('click', () => {
        this.handlers?.onStartReview();
      });
  }
}
